import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Surface } from '@vst/beam';

import {
  configureAddon,
  getEicMessage,
  selectAddOns,
  selectCurrentEasyCareProduct,
  selectCurrentShopAddon,
  setDisclosureChecked,
  setEicMessageError,
  setErrorDisclosureCheck,
  setScrollToConsent,
} from '@app/redux/addOns';
import { selectConfig } from '@app/redux/config';

import { ProductType } from '@mfe/shared/schema-types';

import AddonCart from '../../components/AddonCart';
import DisclosureContent from '../../components/DisclosureContent';
import { SingleFlowProps } from '../../components/SingleAddonContent';
import { ADDON_PRODUCT_KIND } from '../../../../constants';

import styles from '../../styles/disclosure-page.module.scss';

const DisclosureAddOns = ({
  handleContinue,
  handleBack,
  continueButtonLabel,
  isDesktop,
  isLastStep,
}: SingleFlowProps) => {
  const dispatch = useDispatch();

  const { isPWA } = useSelector(selectConfig);
  const currentShopAddon = useSelector(selectCurrentShopAddon);
  const { configureAddonSuccess, configureAddonLoading, eicMessageLoading, eicMessageSuccess, scrollToConsent } =
    useSelector(selectAddOns);
  const chosenEasyCareProduct = useSelector(selectCurrentEasyCareProduct);
  const isVoice = currentShopAddon?.kind === ADDON_PRODUCT_KIND.voice;
  const consentRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isPWA) return;
    window.scrollTo(0, 0);
  }, []);

  const addon = !currentShopAddon?.is_grouped_product ? currentShopAddon : chosenEasyCareProduct;

  // Offer ID exists only on the top-most level group product for grouped EasyCare
  const offerId = currentShopAddon?.offer_id;

  const purchasedProduct =
    currentShopAddon?.is_grouped_product && chosenEasyCareProduct ? chosenEasyCareProduct : currentShopAddon;

  // Skipped once configured, so returning to this step does not re-issue the call
  useEffect(() => {
    if (configureAddonSuccess) return;

    dispatch(
      configureAddon({
        productName: (isVoice ? currentShopAddon?.id : purchasedProduct?.name) ?? '',
        productTypeId: (isVoice ? currentShopAddon?.id : purchasedProduct?.id) ?? '',
        shoppingCartId: offerId ?? '',
        rootProductTypeId: currentShopAddon?.id ?? '',
      })
    );
  }, [dispatch, isVoice, configureAddonSuccess, currentShopAddon, purchasedProduct, offerId]);

  useEffect(() => {
    if (configureAddonSuccess && !eicMessageSuccess) {
      dispatch(getEicMessage({ shoppingCartId: offerId }));
    }
  }, [dispatch, configureAddonSuccess, eicMessageSuccess, offerId]);

  useEffect(() => {
    return () => {
      dispatch(setDisclosureChecked(false));
      dispatch(setErrorDisclosureCheck(false));
      dispatch(setEicMessageError(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (scrollToConsent && consentRef?.current) {
      consentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      dispatch(setScrollToConsent(false));
    }
  }, [dispatch, scrollToConsent]);

  if (!currentShopAddon) {
    return null;
  }

  return (
    <Surface variant={!isDesktop ? 'primary' : 'secondary'} className={styles['page']}>
      <div className={styles['content']}>
        <Surface radius="16px" className={styles['leftCard']}>
          <DisclosureContent
            handleBack={handleBack}
            handleContinue={handleContinue}
            addon={addon}
            consentRef={consentRef}
            submitButtonLabel={continueButtonLabel}
          />
        </Surface>
        {isDesktop && (
          <Surface radius="16px" className={styles['rightCard']}>
            <AddonCart
              isLastStep={isLastStep}
              displayTC={false}
              isSubmitDisabled={eicMessageLoading || configureAddonLoading}
              handleClick={handleContinue}
              addon={currentShopAddon}
              submitButtonLabel={continueButtonLabel}
              selectedEasyCareProduct={chosenEasyCareProduct as ProductType}
            />
          </Surface>
        )}
      </div>
    </Surface>
  );
};

export default DisclosureAddOns;
